import { initializeApp } from 'firebase/app';
import { getAI, getGenerativeModel, GoogleAIBackend } from "firebase/ai";
import { getAuth } from 'firebase/auth';
import { getFirestore, enableMultiTabIndexedDbPersistence } from 'firebase/firestore';
import Router, { Route, Switch } from 'crossroad';
import AppPage from './navigation/AppPage';
import AboutPage from './navigation/AboutPage';
import HomePage from './navigation/HomePage';
import LoginPage from './navigation/LoginPage';
import './index.css';
import { firebaseConfig, isLocal, shouldAllowLocalFunctions } from './lib/constants';
import { AIContext, AuthContext, DBContext, StorageContext } from './lib/context';
import { initializeDarkMode } from './lib/utils/utils';
import { getStorage } from 'firebase/storage';
import { initializeAnalytics, setAnalyticsUserId, setAnalyticsUserProperties } from './lib/analytics';
import { Toaster } from './components/ui/sonner';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { useEffect } from 'react';

export default function Website() {
    const app = initializeApp(firebaseConfig);
    const auth = getAuth(app);
    const db = getFirestore(app);
    const functions = getFunctions(app);

    if (shouldAllowLocalFunctions && isLocal) {
        connectFunctionsEmulator(functions, 'localhost', 5001);
    }

    // Enable multi-tab offline persistence
    enableMultiTabIndexedDbPersistence(db).catch((err) => {
        if (err.code === 'failed-precondition') {
            // Multiple tabs open, persistence can only be enabled in one tab at a time.
            console.warn('Firestore persistence failed: Multiple tabs open.');
        } else if (err.code === 'unimplemented') {
            // The current browser does not support all of the features required to enable persistence
            console.warn('Firestore persistence failed: Browser not supported.');
        }
    });

    const storage = getStorage(app);
    const ai = getAI(app, { backend: new GoogleAIBackend() });
    const geminiFlashModel = getGenerativeModel(ai, { model: 'gemini-3-flash-preview' });
    // const imagenModel = getImagenModel(ai, { model: 'imagen-3.0-generate-002' });
    initializeDarkMode();

    // Initialize Analytics and Performance Monitoring
    initializeAnalytics(app);

    // Track user authentication state for analytics
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setAnalyticsUserId(user.uid);
                setAnalyticsUserProperties({
                    signup_date: user.metadata.creationTime,
                    last_login: user.metadata.lastSignInTime,
                });
            } else {
                setAnalyticsUserId(null);
            }
        });

        return unsubscribe;
    }, [auth]);

    return (
        <AIContext value={{ ai, geminiFlashModel }}>
            <AuthContext value={auth}>
                <DBContext value={db}>
                    <StorageContext value={storage}>
                        <Router scrollUp>
                            <Switch redirect="/">
                                <Route path="/app/:rest*" component={AppPage} />
                                <Route path="/home" component={HomePage} />
                                <Route path="/login" component={LoginPage} />
                                {/* <Route path="/about" component={AboutPage} /> */}
                                <Route path="/" component={HomePage} />
                            </Switch>
                        </Router>
                    </StorageContext>
                </DBContext>
            </AuthContext>
            <Toaster position="top-right" closeButton richColors />
        </AIContext>
    );
}